import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { vocabulary, phrases, translateWord, getPhrase } from './translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('dse-learn-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('dse-learn-language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'sw' : 'en');
  };

  // Basic translation function - t(english, swahili)
  const t = (en, sw) => language === 'en' ? en : sw;

  // Translate a single word using the vocabulary dictionary
  const tw = useCallback((word) => {
    if (language === 'en') return word;
    return translateWord(word, 'sw');
  }, [language]);

  // Get a predefined phrase by key
  const phrase = useCallback((key) => {
    return getPhrase(key, language);
  }, [language]);

  // Get vocabulary for a term (returns {en, sw} object)
  const vocab = useCallback((englishTerm) => {
    const swahiliTerm = vocabulary[englishTerm.toLowerCase()] || englishTerm;
    return {
      en: englishTerm,
      sw: swahiliTerm,
      current: language === 'en' ? englishTerm : swahiliTerm
    };
  }, [language]);

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,        // t(english, swahili) - inline translation
    tw,       // tw(word) - translate single word from vocabulary
    phrase,   // phrase(key) - get predefined phrase
    vocab,    // vocab(term) - get vocabulary entry
    vocabulary, // Full vocabulary dictionary
    phrases,    // Full phrases dictionary
    isSwahili: language === 'sw',
    isEnglish: language === 'en'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
